<template>
    <cms-component v-if="!isComponentEmpty" v-bind="$props" :style="style">
        <component class="cms-text" :is="componentTag" :class="componentClass" :data-hoverable="isEditMode ? 'true' : null">
            <template v-if="isEditMode && !$store.state.comparison.enabled">
                <btn-selector :block="block">
                    {{ compId }} content
                    <template #menu>
                        <b-dropdown-item @click="toggleHtmlEditor" size="xs">
                            {{ isHtmlEditorEnabled ? 'Hide' : 'Show' }} HTML Editor
                        </b-dropdown-item>
                    </template>
                </btn-selector>

                <span class="review-debug" v-if="$store.state.display.review">&#60;{{ componentTag }}&#62;</span>
            </template>

            <slot name="prepend"></slot>

            <i v-if="component.mediaProxy.leftIcon" class="fa-fw mr-1" :class="[fontAwesomePrefix, component.mediaProxy.leftIcon]"/>
            <span v-if="$tr(component.mediaProxy.prefix)">{{ $tr(component.mediaProxy.prefix) }}</span>

            <span v-if="useBinding && component.textField" v-html="dynamicText"/>
            <span v-else-if="component.widget" v-html="widgetText"/>
            <cms-plugins-liquid-renderer v-else-if="isTextContainingLiquid" :value="text" :item="boundItem" :block="block" :component="component"/>
            <template v-else-if="isEditMode">
                <text-compare-suggestion v-if="$store.state.comparison.enabled" :component="component"/>
                <span v-else class="text-editor" ref="textEditor" contenteditable :class="{'empty-placeholder': isUserTextEmpty}" v-html="text"
                      @input="edited" @blur="onBlur" @paste="onPaste" @click="focusText"/>
            </template>
            <span v-else class="text-renderer" v-html="text"/>

            <span v-if="$tr(component.mediaProxy.suffix)">{{ $tr(component.mediaProxy.suffix) }}</span>
            <i v-if="component.mediaProxy.rightIcon" class="fa-fw ml-1" :class="[fontAwesomePrefix, component.mediaProxy.rightIcon]"/>

            <slot name="append"></slot>
        </component>

        <html-editor v-if="isEditMode && (isHtmlEditorEnabled || isTextContainingLiquid && selected)" :value="textForEditor" :max-height="maxEditorHeight"
                     @input="onHtmlEditorInput" @blur="onHtmlEditorBlur" class="mt-2"/>
    </cms-component>
</template>

<script>
import CmsComponent from "@/components/editor/rendering/components/cms-component.vue";
import moment from "moment";
import BtnSelector from "@/components/btn-selector.vue";
import {swal} from "vue-swal";
import EStatusBadge from "@vue-components/components/e-status-badge.vue";
import CmsPluginsLiquidRenderer from "@components/editor/rendering/plugins/cms-plugins-liquid-renderer.vue";

export default {
    name: `cms-component-text`,
    extends: CmsComponent,
    components: {
        CmsPluginsLiquidRenderer,
        EStatusBadge, BtnSelector, CmsComponent,
        HtmlEditor: () => import(`@/components/editor/html-editor.vue`),
        TextCompareSuggestion: () => import(`../../text-compare-suggestion.vue`)
    },
    props: {
        placeholder: {type: String, default: `Text|Texte`},
        highlightRegexp: {type: RegExp, default: null}
    },
    data() {
        return {
            text: ``,
            textForEditor: ``,
            widgetText: Math.random(),
            refreshInterval: null,
            debounceTimer: null,
            blurTimer: null,
            maxEditorHeight: 600 // px
        };
    },

    computed: {
        componentTag() {
            return this.component.getComponentTag();
        },
        sourceText() {
            return this.isEditMode ? this.component.getText(this.displayLanguage) : this.$tr(this.component.text);
        },
        componentClass() {
            switch (this.component.mediaProxy.preset) {
                case `p1`:
                case `p2`:
                case `p3`:
                case `p4`:
                case `p5`:
                case `p6`:
                    return this.component.mediaProxy.preset;
                default:
                    return ``;
            }
        },
        isComponentEmpty() {
            if (this.isEditMode) {
                return false;
            }

            if (this.useBinding) {
                return this.dynamicText?.trim() === ``;
            } else if (this.component.widget) {
                return false;
            } else {
                return this.isUserTextEmpty;
            }
        },
        isUserTextEmpty() {
            return !this.text || this.text === `<br>`;
        },
        isTextContainingLiquid() {
            return this.text?.indexOf(`{{`) >= 0 || this.text?.indexOf(`{%`) >= 0;
        },
        dynamicText() {
            if (!this.component.textField)
                return;

            let value = this.boundItem.getField(this.component.textField);
            if (!value)
                return value;

            if (this.component.format) {
                const values = this.component.format.split(`|`);
                if (values.length > 1) {
                    switch (values[0]) {
                        case `moment`:
                            return moment(value).format(values[1]);
                        case `tel`:
                            return value
                                .replace(/\D/g, ``)
                                .replace(/(\d*)(\d{3})(\d{3})(\d{4})$/, (s, a, b, c, d) => `+${a} (${b}) ${c}-${d}`)
                                .replace(/\+(1\b|\s)\s*/, ``);
                    }
                }
            }
            if (this.highlightRegexp && this.component.textField === `name` && this.boundItem.matchSearchName(this.highlightRegexp)) {
                return this.boundItem.highlightSearchText(this.$tr(value), this.highlightRegexp);
            }
            return this.$tr(value);
        },
        style() {
            let style = {
                fontWeight: this.component.bold ? `bold` : `400`,
                fontStyle: this.component.italic ? `italic` : `normal`,
                textDecoration: this.component.underline ? `underline` : `none`,
                textTransform: this.component.uppercase ? `uppercase` : `none`
            };
            if (this.component.bold) {
                style[`--body-font-weight`] = `bold`;
                style[`--headlines-font-weight`] = `bold`;
                style[`--titles-font-weight`] = `bold`;
            }
            if (this.component.color && this.component.color !== `default`) {
                const color = this.cmsWebsite.getColor(this.component.color);
                style[`--scheme-paragraph-color`] = color;
                style[`--scheme-title-color`] = color;
            }
            if (this.isEditMode) {
                style[`--text-empty-placeholder`] = `"[${this.$tr(this.placeholder)}]"`;
            }
            return style;
        },
        isHtmlEditorEnabled() {
            const blockId = this.block?.getId();
            return this.$store.state.editRawHtml || (blockId && this.$store.state.blockHtmlEditorEnabled[blockId]);
        }
    },
    mounted() {
        this.init();
    },
    created() {
        this.init();
    },
    beforeDestroy() {
        if (this.refreshInterval) {
            clearInterval(this.refreshInterval);
        }
    },
    methods: {
        async init() {
            if (this.isEditMode) {
                this.text = this.component.getText(this.displayLanguage);
            } else {
                this.text = this.$tr(this.component.text);

                if (this.component.widget === `time`) {
                    if (this.refreshInterval) {
                        clearInterval(this.refreshInterval);
                    }
                    this.refreshInterval = setInterval(this.updateWidget.bind(this), 30 * 1000);
                }
            }
            if (this.component.widget) {
                this.updateWidget();
            }

            // sync editor model + preview
            this.textForEditor = this.text || ``;
        },
        updateWidget() {
            switch (this.component.widget) {
                case `date`:
                    this.widgetText = moment().format(`ll`);
                    break;
                case `time`:
                    this.widgetText = moment().format(`LT`);
                    break;
            }
        },
        edited() {
            clearTimeout(this.debounceTimer);
            this.debounceTimer = setTimeout(this.updateText, 300);
        },
        updateText() {
            clearTimeout(this.debounceTimer);

            if (this.$refs.textEditor) {
                if (typeof this.component.newText !== `object`) {
                    this.component.newText = {};
                }
                this.component.newText[this.displayLanguage] = this.$refs.textEditor.innerHTML;
            }
        },
        onBlur() {
            // Next Tick is not enough, important for color/link picker, to leave enough time to avoid breaking selection ref when updating text
            clearTimeout(this.blurTimer);
            this.blurTimer = setTimeout(() => {
                // ugly but not sure we want to add a store variable just for this edge case
                if (!window.disableTextBlur) {
                    this.updateText();
                    this.component.applyChanges();
                    this.text = this.component.getText(this.displayLanguage);
                }
            }, 100);
        },
        onHtmlEditorInput(value) {
            this.textForEditor = value;
            this.text = value;
            this.editedHtml(value);
        },
        onHtmlEditorBlur() {
            this.component.applyChanges();
            this.init();
        },
        editedHtml(value) {
            if (typeof this.component.newText !== `object`) {
                this.component.newText = {};
            }
            this.component.newText[this.displayLanguage] = value;
        },
        toggleHtmlEditor() {
            const blockId = this.block?.getId();
            if (blockId) {
                this.$store.commit(`toggleBlockHtmlEditor`, blockId);
            }
        },
        onPaste(e) {
            const html = (e.clipboardData && e.clipboardData.getData(`text/html`)) || ``;
            const text = (e.clipboardData && e.clipboardData.getData(`text/plain`)) || ``;

            if (!html || !this.containsInlineFormatting(html)) {
                e.preventDefault();
                this.insertAtCursorText(text);
                this.edited({target: {innerHTML: this.$refs.textEditor.innerHTML}});
                return;
            }

            e.preventDefault();

            const doPlain = () => this.insertAtCursorText(text);
            const doSanitized = () => this.insertAtCursorHTML(html);

            swal({
                title: this.$tr(`Paste formatting?|Coller avec format?`),
                text: this.$tr(`The content you pasted contains formatting. Keep styles or paste as plain text?|Le contenu copié contient du format. Voulez-vous coller seulement le texte ?`),
                icon: `info`,
                buttons: {
                    cancel: this.$tr(`Cancel|Annuler`),
                    no: {
                        value: `with-format`,
                        text: this.$tr(`Keep Formatting|Garder format`),
                        className: `btn-default`
                    },
                    yes: {
                        value: `text-only`,
                        text: this.$tr(`Text only|Texte seulement`),
                        className: `btn-primary`
                    }
                }
            })
                .then((format) => {
                    if (!format)
                        return;

                    if (format === `with-format`) {
                        doSanitized();
                    } else if (format === `text-only`) {
                        doPlain();
                    }
                    this.edited({target: {innerHTML: this.$refs.textEditor.innerHTML}});
                });
        },
        insertAtCursorHTML(html) {
            const range = window.getSelection().getRangeAt(0);
            range.deleteContents();
            const tmp = document.createElement(`div`);
            tmp.innerHTML = html;
            const frag = document.createDocumentFragment();
            let node, lastNode;
            while ((node = tmp.firstChild)) {
                lastNode = frag.appendChild(node);
            }
            range.insertNode(frag);
            if (lastNode) {
                range.setStartAfter(lastNode);
                range.collapse(true);
                const sel = window.getSelection();
                sel.removeAllRanges();
                sel.addRange(range);
            }
        },
        insertAtCursorText(text) {
            const selection = window.getSelection();
            if (!selection.rangeCount) {
                return;
            }

            const range = selection.getRangeAt(0);
            range.deleteContents();

            const textNode = document.createTextNode(text || ``);
            range.insertNode(textNode);

            range.setStartAfter(textNode);
            range.collapse(true);
            selection.removeAllRanges();
            selection.addRange(range);
        },
        containsInlineFormatting(html) {
            return /style=|class=|<(span|font|o:p)\b|mso-|google|Apple-converted-space/i.test(html);
        },
        escapeHtml(s) {
            return (s || ``)
                .replace(/&/g, `&amp;`)
                .replace(/</g, `&lt;`)
                .replace(/>/g, `&gt;`);
        },
        focusText() {
            if (this.$refs.textEditor && this.isUserTextEmpty) {
                const element = this.$refs.textEditor;
                const range = document.createRange();
                range.selectNodeContents(element);

                let selection = window.getSelection();
                selection.removeAllRanges();
                selection.addRange(range);
            }
        }
    },
    watch: {
        block() {
            this.init();
        },
        displayLanguage() {
            this.init();
        },
        sourceText(value) {
            const isTyping = this.$refs.textEditor && document.activeElement === this.$refs.textEditor;
            if (value === this.text || isTyping)
                return;
            this.init();
        }
    }
};
</script>

<style lang="scss">
.cms-component-text {
    width: 100%;

    h1, h2, h3, h4, h5, h6 {
        &.cms-text {
            color: var(--scheme-title-color);
        }
    }

    .cms-text {
        position: relative;
        width: 100%;
        color: var(--scheme-paragraph-color);
        display: flex;
        flex-direction: row;

        &:hover, &:focus-within {
            .btn-selector {
                display: block;
            }

            &[data-hoverable="true"] {
                outline: var(--border-width) solid var(--color-editor-focus) !important;
                outline-offset: calc(var(--border-width) * 2);
            }
        }

        .text-editor {
            cursor: text;
            min-width: 20px;
            min-height: 20px;
            flex: 1;
            user-select: text;

            * {
                user-select: text;
            }

            &:not(:focus).empty-placeholder {
                &:before {
                    content: var(--text-empty-placeholder);
                    opacity: 0.65;
                }
            }

            &:focus {
                outline: var(--border-width) solid var(--color-editor-focus) !important;
                outline-offset: var(--border-width);
            }
        }

        .text-renderer {
            flex: 1;
        }

        .search-highlight {
            background: rgba(255, 214, 10, 0.55);
            border-radius: 2px;
            padding: 0 1px;
        }

        a {
            display: inline-flex;
            color: inherit;
            text-decoration: underline !important;
        }

        *, *:focus, *:active, *:focus-visible {
            outline: none;
        }
    }
}

.highlight-links {
    .cms-component-text .cms-text a {
        background-color: #ffffb080 !important;
        text-decoration-color: #dccc1f !important;
    }
}
</style>
